import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { IPaymentMethod } from '@core/interface';
import { PaymentStatusEnum } from '@core/enums';
import { AlertInformationComponent } from '@agenda-customer-ui/shared';
import { FdsCurrencyPipe } from '../../../../pipes/currency/currency-status.pipe';
import { DialogComponent, ButtonLibComponent } from '@components/atoms';

const PAID_STATUSES: PaymentStatusEnum[] = [
  PaymentStatusEnum.APPROVED,
  PaymentStatusEnum.CHARGED_BACK,
  PaymentStatusEnum.AUTHORIZED,
];

const PENDING_STATUSES: PaymentStatusEnum[] = [
  PaymentStatusEnum.PENDING,
  PaymentStatusEnum.IN_PROCESS,
  PaymentStatusEnum.IN_MEDIATION,
  PaymentStatusEnum.REJECTED,
  PaymentStatusEnum.CANCELLED,
  PaymentStatusEnum.ERROR,
];

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app-payment-method-item',
  standalone: true,
  imports: [CommonModule, TranslateModule, AlertInformationComponent, FdsCurrencyPipe, DialogComponent, ButtonLibComponent],
  templateUrl: './payment-method-item.component.html',
  styleUrl: './payment-method-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentMethodItemComponent {
  payment = input.required<IPaymentMethod>();
  showPrices = input<boolean>(true);

  readonly isModalOpen = signal(false);

  readonly mpIconPath = '../../../../../../../../../assets/lib/icons/payments/gateway/mp.svg';
  readonly pendingIconPath = '../../../../../../../../../assets/lib/icons/payments/pending-icon.svg';

  readonly isRefunded = computed(() =>
    this.payment().status?.toLowerCase() === PaymentStatusEnum.REFUNDED
  );

  readonly isDealer = computed(() =>
    this.payment().status?.toLowerCase() === PaymentStatusEnum.DEALER
  );

  readonly isPaid = computed(() =>
    PAID_STATUSES.includes(this.payment().status?.toLowerCase() as PaymentStatusEnum)
  );

  readonly isPending = computed(() =>
    PENDING_STATUSES.includes(this.payment().status?.toLowerCase() as PaymentStatusEnum)
  );

  readonly statusKey = computed(() =>
    `payment-methods.status.${this.payment().status?.toLowerCase()}`
  );

  readonly totalLabel = computed(() => {
    if (this.isRefunded()) return 'payment-methods.total.refunded';
    if (this.isPaid()) return 'payment-methods.total.paid';
    return 'payment-methods.total.pending';
  });

  readonly formattedDateTime = computed(() => {
    const { paymentDate } = this.payment();
    if (!paymentDate) return null;
    const date = new Date(paymentDate);
    const dd = date.getDate().toString().padStart(2, '0');
    const mm = (date.getMonth() + 1).toString().padStart(2, '0');
    const yyyy = date.getFullYear();
    const hh = date.getHours().toString().padStart(2, '0');
    const min = date.getMinutes().toString().padStart(2, '0');
    return `${dd}/${mm}/${yyyy} - ${hh}:${min}`;
  });

  toggleModal(): void {
    this.isModalOpen.update(open => !open);
  }
}
